/**
 * Directed patches for invoice receiving marks (spec 0033 / request 0122).
 *
 * Not `invoiceService.updateInvoice` — that path requires the full header + items
 * and would refuse status/stock transitions unrelated to these stamps.
 * Header writes go through {@link invoiceService.patchFromSystem} so optimistic
 * `version` still increments (request 0236 §7).
 */
import { rtLogService } from '@rammetec/core';
import { repositoryResolver } from '@rammetec/data-access';
import { INVOICE, } from '../types/invoice.js';
import { auditContext } from './auditContext.js';
import { invoiceService } from './invoiceService.js';
function effectiveCheck(invoice) {
    return invoice.receivingCheck ?? 'none';
}
class InvoiceReceivingService {
    getInvoiceRepo(orgId) {
        return repositoryResolver.resolveWithContext(INVOICE, { orgId });
    }
    async loadAuthorizedIn(orgId, invoiceId) {
        const repo = await this.getInvoiceRepo(orgId);
        const invoice = await repo.getById(invoiceId);
        if (!invoice)
            throw new Error('Nota fiscal não encontrada.');
        if (invoice.direction !== 'IN') {
            throw new Error('Conferência só se aplica a nota de entrada.');
        }
        if (invoice.status !== 'authorized') {
            throw new Error('A nota precisa estar autorizada.');
        }
        return { repo, invoice };
    }
    resolveActor(opts) {
        return opts?.userId ?? auditContext.getUserId();
    }
    /** none → requested. Already requested → no-op. Already checked → error. */
    async requestCheck(orgId, invoiceId, opts) {
        const { repo, invoice } = await this.loadAuthorizedIn(orgId, invoiceId);
        const check = effectiveCheck(invoice);
        if (check === 'checked') {
            throw new Error('Nota já conferida; não é possível solicitar conferência.');
        }
        if (check === 'requested') {
            return invoice;
        }
        const userId = this.resolveActor(opts);
        const now = new Date();
        await invoiceService.patchFromSystem(orgId, invoiceId, {
            receivingCheck: 'requested',
            checkRequestedAt: now,
            checkRequestedBy: userId ?? null,
        }, 'invoiceReceiving.requestCheck');
        const updated = await repo.getById(invoiceId);
        if (!updated)
            throw new Error('Nota não encontrada após solicitar conferência.');
        rtLogService.info('[invoice-receiving] requestCheck', {
            orgId,
            invoiceId,
            userId: userId ?? null,
        });
        return updated;
    }
    /**
     * none|requested → checked. Already checked → no-op.
     * Skipping `requested` is intentional (manual conference outside the system).
     */
    async markChecked(orgId, invoiceId, opts) {
        const { repo, invoice } = await this.loadAuthorizedIn(orgId, invoiceId);
        if (effectiveCheck(invoice) === 'checked') {
            return invoice;
        }
        const userId = this.resolveActor(opts);
        const now = new Date();
        await invoiceService.patchFromSystem(orgId, invoiceId, {
            receivingCheck: 'checked',
            checkedAt: now,
            checkedBy: userId ?? null,
        }, 'invoiceReceiving.markChecked');
        const updated = await repo.getById(invoiceId);
        if (!updated)
            throw new Error('Nota não encontrada após marcar conferida.');
        rtLogService.info('[invoice-receiving] markChecked', {
            orgId,
            invoiceId,
            userId: userId ?? null,
        });
        return updated;
    }
    /**
     * First-write stamp when the "gerar volumes" step is done for this invoice.
     * Means "etapa concluída" — may stamp with no volumes when no item controls volume (0124/0125).
     * Does not mean "fully volumized" (partial materialize is ok — 0121).
     */
    async markVolumesGenerated(orgId, invoiceId) {
        const { repo, invoice } = await this.loadAuthorizedIn(orgId, invoiceId);
        if (invoice.volumesGeneratedAt) {
            return invoice;
        }
        await invoiceService.patchFromSystem(orgId, invoiceId, {
            volumesGeneratedAt: new Date(),
        }, 'invoiceReceiving.markVolumesGenerated');
        const updated = await repo.getById(invoiceId);
        if (!updated)
            throw new Error('Nota não encontrada após marcar volumes.');
        rtLogService.info('[invoice-receiving] markVolumesGenerated', { orgId, invoiceId });
        return updated;
    }
    /**
     * First-write stamp when the "imprimir etiquetas" step is done.
     * Means "etapa concluída" — may stamp with nothing to print (0124/0125).
     * Reprints remain allowed (0033 §6.2).
     */
    async markLabelsPrinted(orgId, invoiceId) {
        const { repo, invoice } = await this.loadAuthorizedIn(orgId, invoiceId);
        if (invoice.labelsPrintedAt) {
            return invoice;
        }
        await invoiceService.patchFromSystem(orgId, invoiceId, {
            labelsPrintedAt: new Date(),
        }, 'invoiceReceiving.markLabelsPrinted');
        const updated = await repo.getById(invoiceId);
        if (!updated)
            throw new Error('Nota não encontrada após marcar etiquetas.');
        rtLogService.info('[invoice-receiving] markLabelsPrinted', { orgId, invoiceId });
        return updated;
    }
    /**
     * Reset all receiving marks (cancel / unpost path).
     * Single place that knows the field list — do not inline elsewhere.
     */
    async clearReceivingMarks(orgId, invoiceId) {
        const repo = await this.getInvoiceRepo(orgId);
        const invoice = await repo.getById(invoiceId);
        if (!invoice)
            throw new Error('Nota fiscal não encontrada.');
        await invoiceService.patchFromSystem(orgId, invoiceId, {
            receivingCheck: 'none',
            checkRequestedAt: null,
            checkRequestedBy: null,
            checkedAt: null,
            checkedBy: null,
            volumesGeneratedAt: null,
            labelsPrintedAt: null,
        }, 'invoiceReceiving.clearReceivingMarks');
        rtLogService.info('[invoice-receiving] clearReceivingMarks', { orgId, invoiceId });
    }
}
export const invoiceReceivingService = new InvoiceReceivingService();
//# sourceMappingURL=invoiceReceivingService.js.map